/* eslint-disable react/no-danger */
import * as React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import useSiteUrl from 'core/hooks/useSiteUrl';
import { FaqItem } from 'lib/types';
import { LogoQuery, RichSnippetsDataType, SchemaType } from './types';
import { getMetaData } from './utils';

/* eslint-disable max-len */
/**
 * we want to have >= 50k pixels when multiplying width and height
 *
 * @link https://developers.google.com/search/docs/data-types/local-business#local_business_properties
 */
/* eslint-enable max-len */

export const query = graphql`
    fragment RichSnippetsData on Location {
        ...LocationData
        description
        schema
        googleReviews {
            averageRating
            bestRating
            totalReviewCount
            worstRating
        }
        metaImages {
            file {
                childImageSharp {
                    square: resize(
                        width: 672
                        height: 672
                        quality: 95
                        cropFocus: ENTROPY
                        fit: COVER
                    ) {
                        src
                        width
                        height
                    }
                    narrow: resize(
                        width: 780
                        height: 585
                        quality: 95
                        cropFocus: ENTROPY
                        fit: COVER
                    ) {
                        src
                        width
                        height
                    }
                    wide: resize(
                        width: 912
                        height: 513
                        quality: 95
                        cropFocus: ENTROPY
                        fit: COVER
                    ) {
                        src
                        width
                        height
                    }
                }
            }
        }
    }
    query RichSnippetLogoQuery {
        logo: file(relativePath: { eq: "logo_with_text.png" }) {
            childImageSharp {
                big: resize(
                    width: 260
                    height: 54
                    quality: 95
                    cropFocus: CENTER
                    fit: COVER
                ) {
                    src
                    width
                    height
                }
                small: resize(
                    width: 224
                    height: 54
                    quality: 95
                    cropFocus: CENTER
                    fit: COVER
                ) {
                    src
                    width
                    height
                }
            }
        }
    }
`;

export type RichSnippetsProps = {
    data?: RichSnippetsDataType;
    faqItems?: FaqItem[];
    schemaType?: SchemaType;
    slug?: string;
};

export default function RichSnippets({
    data,
    faqItems,
    schemaType = 'Location',
    slug,
}: RichSnippetsProps) {
    const siteUrl = useSiteUrl();
    const { logo } = useStaticQuery<LogoQuery>(query);

    if (data && 'schema' in data && data.schema) {
        return (
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: data.schema }}
            />
        );
    }

    const metaData = getMetaData(
        siteUrl,
        schemaType,
        data,
        faqItems,
        logo,
        slug,
    );

    if (!metaData) {
        return null;
    }

    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(metaData) }}
        />
    );
}
